import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Typography,
  MenuItem,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  alpha,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AddItemForm from "../Inventory/ItemForm";
import {
  SalesOrderCreate,
  SalesOrderUpdate,
  SalesOrderDetail,
  getDivisionUnits,
  getSizes,
  getCategories,
  fetchItemsByCategoryAndSubcategory,
  ApprovePartyList,
  GetNextSoNo,
  getAllBOMs,
  getVoltageRatio,
  getRatingSpecifications,
  getWindingMaterials,
  getAllWorkTypes,
} from "../../../Services/Services";
import { debounce } from "lodash";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function SalesOrderEntry() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ── Theme-aware helpers ──
  const disabledBg = isDark
    ? alpha(theme.palette.common.white, 0.06)
    : "#f5f5f5";

  const grandTotalBg = isDark
    ? alpha(theme.palette.success.main, 0.12)
    : "#e8f5e9";

  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.03)
        : "#ffffff"
      : theme.palette.background.paper;

  const [partyOptions, setPartyOptions] = useState([]);
  const [partySearch, setPartySearch] = useState("");
  const [units, setUnits] = useState([]);
  const [file, setFile] = useState(null);
  const [openItemModal, setOpenItemModal] = useState(false);

  // Item Options Map per row index
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(false);

  const [bomOptions, setBomOptions] = useState([]);
  const [bomLoading, setBomLoading] = useState(false);

  // ---------- VOLTAGE / RATING / WINDING STATES ----------
  const [voltageOptions, setVoltageOptions] = useState([]);
  const [voltageSearch, setVoltageSearch] = useState("");
  const [voltageLoading, setVoltageLoading] = useState(false);

  const [ratingOptions, setRatingOptions] = useState([]);
  const [ratingSearch, setRatingSearch] = useState("");
  const [ratingLoading, setRatingLoading] = useState(false);

  const [windingOptions, setWindingOptions] = useState([]);
  const [windingSearch, setWindingSearch] = useState("");
  const [windingLoading, setWindingLoading] = useState(false);

  const [existingFileName, setExistingFileName] = useState("");
  const [hasDraft, setHasDraft] = useState(false);

  const [allCategories, setAllCategories] = useState([]);
  const [rowProductTypeOptions, setRowProductTypeOptions] = useState({});

  const [workTypeOptions, setWorkTypeOptions] = useState([]);
  const [workTypeLoading, setWorkTypeLoading] = useState(false);

  const defaultTerms = [
    { key: "Delivery Period", value: "Within 15 days", is_default: true },
    { key: "Warranty", value: "12 months from delivery", is_default: true },
    { key: "Freight", value: "Included in price", is_default: true },
    { key: "Inspection", value: "By buyer at site", is_default: true },
    { key: "Quality", value: "As per specifications", is_default: true },
    { key: "Rejection/Claim", value: "Allowed within 7 days", is_default: true },
    { key: "Jurisdiction", value: "Raipur", is_default: true },
    { key: "Payment Terms", value: "50% advance, balance on delivery", is_default: true },
    { key: "TCS/TDS", value: "As applicable", is_default: true },
    { key: "Remark", value: "None", is_default: true },
  ];

  const emptySoItem = {
    product_type: null,
    category: null,
    item: null,
    description: "",
    quantity: 0,
    unit: "",
    rate: 0,
    amount: 0,
    dis_per: 0,
  };

  const [formData, setFormData] = useState({
    party: "",
    party_details: null,
    party_name: "",
    project_name: "",
    order_date: "",
    delivery_date: "",
    so_no: "",
    project: "",
    po_number: "",
    bom_id: "",
    bom_name: "",
    priority: "Normal",
    voltage_ratio: "",
    rating_specification: "",
    winding_material: "",
    work_type: "",
    cgst_per: 0,
    sgst_per: 0,
    round_off: 0,
    grand_total: 0,
    total_amount: 0,
    created_by: localStorage.getItem("user_id"),
    terms_and_conditions: defaultTerms,
    items: [{ ...emptySoItem }],
  });

  const { id } = useParams();
  const isEditMode = !!id;

  const navigate = useNavigate();
  const fileInputRef = useRef();
  const location = useLocation();
  const amd = location.state?.amd;

  const [termsAndConditions, setTermsAndConditions] = useState(defaultTerms);

  useEffect(() => {
    fetchDivionUnits();
    fetchBOMs();
    fetchVoltageOptions("");
    fetchRatingOptions("");
    fetchWindingOptions("");
    fetchAllCategories();
    fetchWorkTypes();
  }, []);

  const fetchWorkTypes = async () => {
    setWorkTypeLoading(true);
    try {
      const response = await getAllWorkTypes();
      if (response.success) {
        setWorkTypeOptions(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching work types:", err);
    } finally {
      setWorkTypeLoading(false);
    }
  };

  const fetchAllCategories = async () => {
    try {
      const data = await getCategories(1, "");
      const cats = Array.isArray(data) ? data : data?.results || [];
      setAllCategories(cats);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchBOMs = async () => {
    setBomLoading(true);
    try {
      const response = await getAllBOMs();
      const data = response.results || response || [];
      setBomOptions(data);
    } catch (err) {
      console.error("Error fetching BOMs:", err);
      toast.error("Failed to load BOMs");
    } finally {
      setBomLoading(false);
    }
  };

  const fetchVoltageOptions = async (search = "") => {
    try {
      setVoltageLoading(true);
      const data = await getVoltageRatio(1, search);
      setVoltageOptions(data.results || []);
    } catch (err) {
      console.error("Error fetching voltage ratios:", err);
    } finally {
      setVoltageLoading(false);
    }
  };

  const fetchRatingOptions = async (search = "") => {
    try {
      setRatingLoading(true);
      const data = await getRatingSpecifications(1, search);
      setRatingOptions(data.results || []);
    } catch (err) {
      console.error("Error fetching rating specs:", err);
    } finally {
      setRatingLoading(false);
    }
  };

  const fetchWindingOptions = async (search = "") => {
    try {
      setWindingLoading(true);
      const data = await getWindingMaterials(1, search);
      setWindingOptions(data.results || []);
    } catch (err) {
      console.error("Error fetching winding materials:", err);
    } finally {
      setWindingLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getParties(1, { name: partySearch });
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [partySearch]);

  useEffect(() => {
    const t = setTimeout(() => fetchVoltageOptions(voltageSearch), 400);
    return () => clearTimeout(t);
  }, [voltageSearch]);

  useEffect(() => {
    const t = setTimeout(() => fetchRatingOptions(ratingSearch), 400);
    return () => clearTimeout(t);
  }, [ratingSearch]);

  useEffect(() => {
    const t = setTimeout(() => fetchWindingOptions(windingSearch), 400);
    return () => clearTimeout(t);
  }, [windingSearch]);

  useEffect(() => {
    if (!isEditMode) {
      fetchNextSoNo();
    }
  }, [isEditMode]);

  const fetchDivionUnits = async () => {
    const data = await getDivisionUnits();
    setUnits(data.units || []);
  };

  const getParties = async (pageNum = 1, filters = {}) => {
    try {
      const response = await ApprovePartyList(pageNum, filters);
      const results = Array.isArray(response) ? response : [];
      if (pageNum === 1) {
        setPartyOptions(results);
      } else {
        setPartyOptions((prev) => [...prev, ...results]);
      }
    } catch (err) {
      console.error("Error fetching parties:", err);
    }
  };

  // ── Row Field Handlers ──
  const handleRowCategoryChange = async (index, newCategory) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = {
        ...nextItems[index],
        category: newCategory,
        product_type: null,
        item: null,
        description: "",
        unit: "",
      };
      return { ...prev, items: nextItems };
    });

    setItems((prev) => ({ ...prev, [index]: [] }));

    if (newCategory?.id) {
      try {
        const sizesData = await getSizes("", newCategory.id);
        const list = Array.isArray(sizesData)
          ? sizesData
          : sizesData?.results || [];
        setRowProductTypeOptions((prev) => ({ ...prev, [index]: list }));
      } catch {
        setRowProductTypeOptions((prev) => ({ ...prev, [index]: [] }));
      }
    } else {
      setRowProductTypeOptions((prev) => ({ ...prev, [index]: [] }));
    }
  };

  const handleRowProductTypeChange = async (index, newProductType) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = {
        ...nextItems[index],
        product_type: newProductType,
        item: null,
        description: "",
        unit: "",
      };
      return { ...prev, items: nextItems };
    });

    if (!newProductType?.id) {
      setItems((prev) => ({ ...prev, [index]: [] }));
      return;
    }

    const catId = formData.items[index]?.category?.id || 0;
    try {
      const itemData = await fetchItemsByCategoryAndSubcategory(
        catId,
        0,
        1,
        "",
        newProductType.id,
      );
      setItems((prev) => ({
        ...prev,
        [index]: itemData?.results || [],
      }));
    } catch {
      setItems((prev) => ({ ...prev, [index]: [] }));
    }
  };

  // ── Edit Order Fetch ──
  useEffect(() => {
    const fetchOrder = async (orderId) => {
      try {
        const response = await SalesOrderDetail(orderId);
        const data = response?.data || response;

        const mappedItems = (data.items || []).map((it) => ({
          category: it.category
            ? { id: it.category, name: it.category_name || "" }
            : null,
          product_type: it.product_type
            ? {
                id: it.product_type.id,
                size: it.product_type.size || it.product_type.name || "",
              }
            : it.size_id || it.size
              ? {
                  id: it.size_id || it.size,
                  size: it.size_name || it.product_type_name || "",
                }
              : null,
          item: it.item
            ? {
                id: it.item,
                name: it.item_name || "",
                item_description: it.description || "",
              }
            : null,
          description: it.description || "",
          quantity: it.quantity || 0,
          unit: it.unit || "",
          rate: it.rate || 0,
          amount: it.amount || 0,
          dis_per: it.dis_per || 0,
        }));

        setFormData({
          so_no: data.so_no || "",
          order_date: data.order_date || "",
          delivery_date: data.delivery_date || "",
          po_number: data.po_number || "",
          priority: data.priority || "Normal",
          voltage_ratio: data.voltage_ratio || "",
          rating_specification: data.rating_specification || "",
          winding_material: data.winding_material || "",
          cgst_per: data.cgst_per || 0,
          sgst_per: data.sgst_per || 0,
          round_off: data.round_off || 0,
          remarks: data.remarks || "",
          project_name: data.project_name || "",
          party: data.party_details?.id || data.party || "",
          party_details: data.party_details || null,
          party_name: data.party_name || data.party_details?.company_name || "",
          bom_id: data.bom || "",
          bom_name: data.bom_name || "",
          work_type: data.work_type || "",
          terms_and_conditions: data.terms_and_conditions || defaultTerms,
          items: mappedItems,
          total_amount: data.total_amount || 0,
          grand_total: data.grand_total || 0,
        });

        setTermsAndConditions(data.terms_and_conditions || defaultTerms);

        // Pre-load dropdown options
        const ptMap = {};
        const itemMap = {};

        await Promise.all(
          mappedItems.map(async (row, index) => {
            const catId = row.category?.id;
            const ptId = row.product_type?.id;

            if (catId) {
              try {
                const sizesData = await getSizes("", catId);
                ptMap[index] = Array.isArray(sizesData)
                  ? sizesData
                  : sizesData?.results || [];
              } catch {
                ptMap[index] = [];
              }
            }

            if (ptId) {
              try {
                const itemData = await fetchItemsByCategoryAndSubcategory(
                  catId || 0,
                  0,
                  1,
                  "",
                  ptId,
                );
                let list = itemData?.results || [];
                if (row.item && !list.find((i) => i.id === row.item.id)) {
                  list = [row.item, ...list];
                }
                itemMap[index] = list;
              } catch {
                itemMap[index] = row.item ? [row.item] : [];
              }
            }
          }),
        );

        setRowProductTypeOptions(ptMap);
        setItems(itemMap);
      } catch (error) {
        console.error("Error fetching order details:", error);
        toast.error("Failed to fetch order details.");
      }
    };

    if (id) {
      fetchOrder(id);
    }
  }, [id]);

  const fetchNextSoNo = async () => {
    try {
      const data = await GetNextSoNo();
      if (data && data.so_no) {
        setFormData((prev) => ({
          ...prev,
          so_no: prev.so_no ? prev.so_no : data.so_no,
        }));
      }
    } catch (err) {
      console.error("Error fetching next SO No:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (
        name === "order_date" &&
        prev.delivery_date &&
        value &&
        prev.delivery_date < value
      ) {
        next.delivery_date = "";
      }
      return next;
    });
  };

  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const updatedItems = [...formData.items];
    updatedItems[index][name] = value;

    const qty = parseFloat(updatedItems[index].quantity) || 0;
    const rate = parseFloat(updatedItems[index].rate) || 0;
    const dis_per = parseFloat(updatedItems[index].dis_per) || 0;
    updatedItems[index].amount = qty * rate - (qty * rate * dis_per) / 100;

    const total_amount = updatedItems.reduce(
      (sum, i) => sum + Number(i.amount),
      0,
    );
    const cgst_amt = (total_amount * (formData.cgst_per || 0)) / 100;
    const sgst_amt = (total_amount * (formData.sgst_per || 0)) / 100;
    const grand_total =
      total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
      total_amount: Number(total_amount.toFixed(2)),
      grand_total: Number(grand_total.toFixed(2)),
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptySoItem }],
    }));
  };

  const deleteItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    const total_amount = updatedItems.reduce(
      (sum, i) => sum + Number(i.amount),
      0,
    );
    setFormData((prev) => ({ ...prev, items: updatedItems, total_amount }));
  };

  const handleTermChange = (index, field, value) => {
    const updatedTerms = [...termsAndConditions];
    updatedTerms[index][field] = value;
    setTermsAndConditions(updatedTerms);
  };

  const addTerm = () => {
    setTermsAndConditions([
      ...termsAndConditions,
      { key: "", value: "", is_default: false },
    ]);
  };

  const handleCloseItemModal = async () => {
    setOpenItemModal(false);
  };

  const removeTerm = (index) => {
    const updatedTerms = termsAndConditions.filter((_, i) => i !== index);
    setTermsAndConditions(updatedTerms);
  };

    const handleBOMSelect = async (e, bom) => {
    if (!bom) {
      setFormData((prev) => ({ ...prev, bom_id: "", bom_name: "" }));
      return;
    }

    const components = bom.components || [];

    const mapped = components.map((comp) => {
      const categoryObj = comp.category
        ? typeof comp.category === "object"
          ? comp.category
          : { id: comp.category, name: comp.category_name || "" }
        : null;

      const productTypeObj = comp.product_type
        ? typeof comp.product_type === "object"
          ? {
              id: comp.product_type.id,
              size: comp.product_type.size || comp.product_type.name || "",
            }
          : {
              id: comp.product_type,
              size: comp.product_type_name || "",
            }
        : null;

      const itemObj = comp.item
        ? typeof comp.item === "object"
          ? comp.item
          : { id: comp.item, name: comp.item_name || "" }
        : null;

      const unitObj = comp.unit
        ? typeof comp.unit === "object"
          ? comp.unit
          : { id: comp.unit, name: comp.uom || comp.unit_name || "" }
        : null;

      const qty = parseFloat(comp.quantity) || 0;
      // ✅ backend serializer se aaya price
      const rate = parseFloat(comp.price ?? comp.item?.price ?? 0) || 0;
      const amount = qty * rate;

      return {
        category: categoryObj,
        product_type: productTypeObj,
        item: itemObj,
        description: comp.description || itemObj?.item_description || "",
        quantity: qty,
        unit: unitObj ? unitObj.id : "",
        rate,      // ✅ was 0
        amount,    // ✅ was 0
        dis_per: 0,
      };
    });

    const total_amount = mapped.reduce((sum, i) => sum + Number(i.amount || 0), 0);
    const cgst_amt = (total_amount * (formData.cgst_per || 0)) / 100;
    const sgst_amt = (total_amount * (formData.sgst_per || 0)) / 100;
    const grand_total =
      total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);

    setFormData((prev) => ({
      ...prev,
      bom_id: bom.id,
      bom_name: bom.bom_name,
      items: mapped,
      total_amount: Number(total_amount.toFixed(2)),
      grand_total: Number(grand_total.toFixed(2)),
    }));

    // dropdown options preload (same as pehle)
    const ptMap = {};
    const itemMap = {};

    await Promise.all(
      mapped.map(async (row, index) => {
        const catId = row.category?.id;
        const ptId = row.product_type?.id;

        if (catId) {
          try {
            const sizesData = await getSizes("", catId);
            ptMap[index] = Array.isArray(sizesData)
              ? sizesData
              : sizesData?.results || [];
          } catch {
            ptMap[index] = [];
          }
        }

        if (ptId) {
          try {
            const itemData = await fetchItemsByCategoryAndSubcategory(
              catId || 0,
              0,
              1,
              "",
              ptId,
            );
            let list = itemData?.results || [];
            if (row.item && !list.find((i) => i.id === row.item.id)) {
              list = [row.item, ...list];
            }
            itemMap[index] = list;
          } catch {
            itemMap[index] = row.item ? [row.item] : [];
          }
        }
      }),
    );

    setRowProductTypeOptions(ptMap);
    setItems(itemMap);

    // ✅ optional safety: agar BOM me price 0 aaya ho to item master se backfill
    setFormData((prev) => {
      const nextItems = prev.items.map((row, index) => {
        if (Number(row.rate) > 0) return row;
        const list = itemMap[index] || [];
        const found = list.find((i) => i.id === row.item?.id || i.id === row.item);
        if (!found) return row;
        const rate = parseFloat(found.price ?? found.new_price ?? found.rate ?? 0) || 0;
        const qty = parseFloat(row.quantity) || 0;
        return { ...row, rate, amount: qty * rate };
      });
      const total_amount = nextItems.reduce((s, i) => s + Number(i.amount || 0), 0);
      const cgst_amt = (total_amount * (prev.cgst_per || 0)) / 100;
      const sgst_amt = (total_amount * (prev.sgst_per || 0)) / 100;
      const grand_total =
        total_amount + cgst_amt + sgst_amt + Number(prev.round_off || 0);
      return {
        ...prev,
        items: nextItems,
        total_amount: Number(total_amount.toFixed(2)),
        grand_total: Number(grand_total.toFixed(2)),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append("project_name", formData.project_name);
    formDataToSend.append("party", formData.party);
    formDataToSend.append("order_date", formData.order_date);

    if (amd === "AMD") {
      formDataToSend.append(
        "so_no",
        formData.so_no.endsWith("-AMD") ? formData.so_no : `${formData.so_no}-AMD`
      );
    } else {
      formDataToSend.append("so_no", formData.so_no);
    }

    formDataToSend.append("total_amount", formData.total_amount);
    formDataToSend.append("created_by", localStorage.getItem("user_id"));
    formDataToSend.append("cgst_per", formData.cgst_per);
    formDataToSend.append("sgst_per", formData.sgst_per);
    formDataToSend.append("round_off", formData.round_off);
    formDataToSend.append("grand_total", formData.grand_total || 0);
    formDataToSend.append("po_number", formData.po_number);
    formDataToSend.append("delivery_date", formData.delivery_date || "");
    formDataToSend.append("bom", formData.bom_id || "");

    formDataToSend.append("priority", formData.priority || "Normal");
    formDataToSend.append("voltage_ratio", formData.voltage_ratio || "");
    formDataToSend.append("rating_specification", formData.rating_specification || "");
    formDataToSend.append("winding_material", formData.winding_material || "");
    formDataToSend.append("work_type", formData.work_type || "");

    if (file && file instanceof File) {
      formDataToSend.append("project", file);
    }

    const filteredTerms = termsAndConditions.filter(
      (term) => term.key.trim() !== "" && term.value.trim() !== "",
    );

    const cleanedItems = formData.items.map((item) => ({
      category: item.category?.id || item.category || null,
      size: item.product_type?.id || item.product_type || null,
      product_type_id: item.product_type?.id || item.product_type || null,
      item: item.item?.id || item.item || null,
      description: item.description || "",
      quantity: Number(item.quantity) || 0,
      unit: item.unit?.id || item.unit || null,
      rate: Number(item.rate) || 0,
      amount: Number(item.amount) || 0,
      dis_per: Number(item.dis_per) || 0,
    }));

    formDataToSend.append("terms_and_conditions", JSON.stringify(filteredTerms));
    formDataToSend.append("items", JSON.stringify(cleanedItems));

    try {
      let result;
      if (id) {
        result = await SalesOrderUpdate(id, formDataToSend);
        toast.success("MOS Updated!");
      } else {
        result = await SalesOrderCreate(formDataToSend);
        toast.success("MOS Created!");
      }
      if (result) {
        resetForm();
        setTimeout(() => navigate("/dashboard/saleorderlist"), 1000);
      } else {
        toast.error("Something went wrong.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit MOS.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    sessionStorage.removeItem("soDraftHeader");
    setTermsAndConditions(defaultTerms);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
    setTimeout(() => navigate("/dashboard/saleorderlist"), 1000);
  };

    const handleItemAutocompleteChange = (index, key, value) => {
    const updatedItems = [...formData.items];
    let row = { ...updatedItems[index] };

    if (key === "item") {
      const qty = parseFloat(row.quantity) || 0;
      // ✅ item master se price
      const rate =
        value != null
          ? parseFloat(value.price ?? value.new_price ?? value.rate ?? row.rate) || 0
          : 0;
      const dis_per = parseFloat(row.dis_per) || 0;
      const amount = qty * rate - (qty * rate * dis_per) / 100;

      row = {
        ...row,
        item: value,
        description:
          value?.description || value?.item_description || row.description || "",
        rate,
        amount,
      };
    }

    updatedItems[index] = row;
    const total_amount = updatedItems.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );
    const cgst_amt = (total_amount * (formData.cgst_per || 0)) / 100;
    const sgst_amt = (total_amount * (formData.sgst_per || 0)) / 100;
    const grand_total =
      total_amount + cgst_amt + sgst_amt + Number(formData.round_off || 0);

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
      total_amount: Number(total_amount.toFixed(2)),
      grand_total: Number(grand_total.toFixed(2)),
    }));
  };
  
  const handleItemInputChange = useCallback(
    debounce(async (index, row, newValue, type) => {
      if (type === "category") return;

      if (type === "item" && row.product_type?.id) {
        const itemData = await fetchItemsByCategoryAndSubcategory(
          row.category?.id || 0,
          0,
          1,
          newValue || "",
          row.product_type.id,
        );
        setItems((prev) => ({
          ...prev,
          [index]: itemData?.results || [],
        }));
      }
    }, 500),
    [],
  );

  const handleSaveDraft = () => {
    if (isEditMode) return;
    sessionStorage.setItem(
      "soDraftHeader",
      JSON.stringify({ formData, terms: termsAndConditions }),
    );
    setHasDraft(true);
    toast.info("MOS draft saved!");
  };

  const handleDeleteDraft = () => {
    sessionStorage.removeItem("soDraftHeader");
    setHasDraft(false);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
    setTermsAndConditions(defaultTerms);
    setFormData((prev) => ({
      ...prev,
      party: "",
      party_details: null,
      party_name: "",
      project_name: "",
      order_date: "",
      delivery_date: "",
      project: "",
      po_number: "",
      bom_id: "",
      bom_name: "",
      priority: "Normal",
      voltage_ratio: "",
      rating_specification: "",
      winding_material: "",
      work_type: "",
      cgst_per: 0,
      sgst_per: 0,
      round_off: 0,
      grand_total: 0,
      total_amount: 0,
      so_no: prev.so_no,
      created_by: localStorage.getItem("user_id"),
      terms_and_conditions: defaultTerms,
      items: [{ ...emptySoItem }],
    }));
    toast.info("Draft deleted");
  };

  useEffect(() => {
    if (isEditMode) return;
    const draftHeader = sessionStorage.getItem("soDraftHeader");
    if (!draftHeader) return;
    try {
      const parsed = JSON.parse(draftHeader);
      const draftForm = parsed.formData || parsed;

      setFormData((prev) => ({
        ...prev,
        ...draftForm,
        so_no: prev.so_no || draftForm.so_no || "",
      }));

      if (parsed.terms) {
        setTermsAndConditions(parsed.terms);
      } else if (draftForm.terms_and_conditions) {
        setTermsAndConditions(draftForm.terms_and_conditions);
      }
      setHasDraft(true);
      toast.info("Loaded MOS draft");
    } catch (e) {
      console.error("Failed to parse draft:", e);
    }
  }, [isEditMode]);

  return (
    <Container maxWidth="xl">
      <ToastContainer theme={isDark ? "dark" : "light"} />

      <form onSubmit={handleSubmit} autoComplete="off">
        <Box sx={{ bgcolor: "background.default", minHeight: "100%" }}>
          {/* ── Top Action Bar ── */}
          <Grid
            container
            spacing={1}
            alignItems="center"
            sx={{
              mb: 1,
              mt: { xs: 3, md: 0 },
              position: "sticky",
              top: 0,
              zIndex: 1100,
              pt: 1,
              pb: 1,
            }}
          >
            <Grid item sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />

            {/* Draft */}
            <Grid item xs={6} sm="auto">
              <Button
                fullWidth
                variant="outlined"
                onClick={hasDraft ? handleDeleteDraft : handleSaveDraft}
                sx={{
                  height: 32,
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  borderRadius: 0,
                  color: hasDraft ? "error.main" : "#ff8400",
                  borderColor: hasDraft ? "error.main" : "#ff8400",
                  whiteSpace: "nowrap",
                  minWidth: { sm: 110 },
                  "&:hover": {
                    borderColor: hasDraft ? "error.dark" : "#e07600",
                    backgroundColor: hasDraft
                      ? (t) => alpha(t.palette.error.main, 0.05)
                      : isDark
                        ? alpha("#ff8400", 0.1)
                        : "rgba(255,132,0,0.05)",
                  },
                }}
              >
                {hasDraft ? "Delete Draft" : "Draft"}
              </Button>
            </Grid>

            {/* Submit */}
            <Grid item xs={6} sm="auto">
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{
                  height: 32,
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  borderRadius: 0,
                  whiteSpace: "nowrap",
                  minWidth: { sm: 120 },
                }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : "Submit MOS"}
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1, mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="MOS No."
                name="so_no"
                value={formData.so_no || ""}
                disabled
                size="small"
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0, bgcolor: disabledBg },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="PO Number"
                name="po_number"
                value={formData.po_number}
                onChange={handleChange}
                required
                size="small"
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="date"
                name="order_date"
                label="PO Date"
                value={formData.order_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
                size="small"
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={partyOptions}
                getOptionLabel={(option) => option?.company_name || option?.name || ""}
                value={
                  partyOptions.find((o) => o.id === formData.party) ||
                  (formData.party
                    ? {
                        id: formData.party,
                        company_name: formData.party_details?.company_name || formData.party_name || "",
                      }
                    : null)
                }
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id || opt?.id === val}
                onChange={(e, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    party: newValue ? newValue.id : "",
                    party_details: newValue || null,
                    party_name: newValue ? newValue.company_name || newValue.name : "",
                  }));
                }}
                onInputChange={(e, val, reason) => {
                  if (reason === "input") setPartySearch(val);
                }}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Debtor"
                    required
                    sx={{
                      "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="MOS"
                name="project_name"
                value={formData.project_name}
                onChange={handleChange}
                required
                size="small"
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <Box>
                <TextField
                  fullWidth
                  type="file"
                  name="project"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setExistingFileName("");
                  }}
                  inputRef={fileInputRef}
                  InputLabelProps={{ shrink: true }}
                  label={existingFileName ? "Change File" : "Upload File"}
                  size="small"
                  inputProps={{ accept: "*/*" }}
                  sx={{
                    "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                    "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                  }}
                />
                {existingFileName && formData.project && isEditMode && (
                  <Box mt={0.3} display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {existingFileName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => window.open(formData.project, "_blank")}
                      sx={{ p: 0.2 }}
                    >
                      <VisibilityIcon sx={{ fontSize: "0.85rem" }} color="primary" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={bomOptions}
                getOptionLabel={(option) => option?.bom_name || option?.bom_no || ""}
                loading={bomLoading}
                value={
                  formData.bom_id
                    ? bomOptions.find((o) => o.id === formData.bom_id) ||
                      (formData.bom_name
                        ? { id: formData.bom_id, bom_name: formData.bom_name }
                        : null)
                    : null
                }
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id || opt?.id === val}
                onChange={handleBOMSelect}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="BOM"
                    sx={{
                      "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {bomLoading ? <CircularProgress color="inherit" size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="date"
                name="delivery_date"
                label="Date of Delivery"
                value={formData.delivery_date || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                inputProps={{ min: formData.order_date || undefined }}
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={voltageOptions}
                getOptionLabel={(opt) => opt?.name || opt || ""}
                value={
                  voltageOptions.find((o) => o.name === formData.voltage_ratio) ||
                  (formData.voltage_ratio ? { name: formData.voltage_ratio } : null)
                }
                isOptionEqualToValue={(opt, val) => (opt?.name || opt) === (val?.name || val)}
                onChange={(e, newVal) =>
                  setFormData((prev) => ({
                    ...prev,
                    voltage_ratio: newVal ? newVal.name : "",
                  }))
                }
                onInputChange={(e, val, reason) => {
                  if (reason === "input") setVoltageSearch(val);
                }}
                loading={voltageLoading}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Voltage Ratio"
                    placeholder="e.g. 11000/433"
                    sx={{
                      "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {voltageLoading ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={ratingOptions}
                getOptionLabel={(opt) => opt?.name || opt || ""}
                value={
                  ratingOptions.find((o) => o.name === formData.rating_specification) ||
                  (formData.rating_specification ? { name: formData.rating_specification } : null)
                }
                isOptionEqualToValue={(opt, val) => (opt?.name || opt) === (val?.name || val)}
                onChange={(e, newVal) =>
                  setFormData((prev) => ({
                    ...prev,
                    rating_specification: newVal ? newVal.name : "",
                  }))
                }
                onInputChange={(e, val, reason) => {
                  if (reason === "input") setRatingSearch(val);
                }}
                loading={ratingLoading}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rating Specification"
                    placeholder="e.g. 500 KVA"
                    sx={{
                      "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {ratingLoading ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <Autocomplete
                options={windingOptions}
                getOptionLabel={(opt) => opt?.name || opt || ""}
                value={
                  windingOptions.find((o) => o.name === formData.winding_material) ||
                  (formData.winding_material ? { name: formData.winding_material } : null)
                }
                isOptionEqualToValue={(opt, val) => (opt?.name || opt) === (val?.name || val)}
                onChange={(e, newVal) =>
                  setFormData((prev) => ({
                    ...prev,
                    winding_material: newVal ? newVal.name : "",
                  }))
                }
                onInputChange={(e, val, reason) => {
                  if (reason === "input") setWindingSearch(val);
                }}
                loading={windingLoading}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Winding Material"
                    placeholder="e.g. Copper"
                    sx={{
                      "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {windingLoading ? <CircularProgress size={14} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Priority"
                name="priority"
                value={formData.priority || "Normal"}
                onChange={handleChange}
                size="small"
                SelectProps={{ MenuProps: { disableScrollLock: true } }}
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              >
                <MenuItem value="Low">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#d97706" }}>
                      Low
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="Normal">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                    <Typography sx={{ fontSize: "0.75rem" }}>Normal</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="High">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#d32f2f" }} />
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#d32f2f" }}>
                      High
                    </Typography>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Work Type"
                name="work_type"
                value={formData.work_type || ""}
                onChange={handleChange}
                required
                size="small"
                SelectProps={{ MenuProps: { disableScrollLock: true } }}
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              >
                {workTypeLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={14} sx={{ mr: 1 }} /> Loading...
                  </MenuItem>
                ) : workTypeOptions.length === 0 ? (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      No work types available
                    </Typography>
                  </MenuItem>
                ) : (
                  workTypeOptions.map((wt) => (
                    <MenuItem key={wt.id} value={wt.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                          {wt.name}
                        </Typography>
                        <Chip
                          label={`${wt.phase_count || 0} phases`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.55rem",
                            fontWeight: 600,
                            bgcolor: isDark
                              ? alpha(theme.palette.primary.main, 0.15)
                              : "#eff6ff",
                            color: "primary.main",
                          }}
                        />
                      </Box>
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
          </Grid>

          {/* ── Items Section Header ── */}
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{
              mt: 2,
              mb: 0.5,
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              color: "primary.main",
              borderBottom: "2px solid",
              borderColor: "primary.main",
              display: "inline-block",
              pb: 0.3,
            }}
          >
            Items
          </Typography>

          {isMobile ? (
            /* ── MOBILE CARD LAYOUT ── */
            <Box sx={{ mb: 2 }}>
              {formData.items.map((item, index) => (
                <Card
                  key={index}
                  elevation={0}
                  sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    backgroundImage: "none",
                    boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "none",
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: "0.85rem", fontWeight: 700, color: "primary.main" }}
                      >
                        Item #{index + 1}
                      </Typography>
                      <IconButton
                        onClick={() => deleteItem(index)}
                        color="error"
                        size="small"
                        sx={{
                          "&:hover": {
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.error.main, 0.15)
                                : "#ffebee",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Divider sx={{ mb: 2, borderColor: "divider" }} />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {/* Category FIRST */}
                      <Autocomplete
                        options={allCategories}
                        getOptionLabel={(opt) => opt?.name || ""}
                        value={item.category || null}
                        onChange={(e, val) => handleRowCategoryChange(index, val)}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Category" size="small" />}
                      />

                      {/* Product Type SECOND */}
                      <Autocomplete
                        options={rowProductTypeOptions[index] || []}
                        getOptionLabel={(opt) => opt?.size || ""}
                        value={item.product_type || null}
                        disabled={!item.category}
                        onChange={(e, val) => handleRowProductTypeChange(index, val)}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        renderInput={(params) => <TextField {...params} label="Product Type" size="small" />}
                      />

                      {/* Item */}
                      <Autocomplete
                        options={items[index] || (item.item ? [item.item] : [])}
                        getOptionLabel={(opt) => opt?.name || ""}
                        value={
                          (items[index] || []).find(
                            (i) => i.id === item.item?.id || i.id === item.item,
                          ) ||
                          item.item ||
                          null
                        }
                        disabled={!item.product_type}
                        onChange={async (e, val, reason) => {
                          if (reason === "clear" || !val) {
                            await handleItemInputChange(index, item, "", "item");
                            handleItemAutocompleteChange(index, "item", null);
                            return;
                          }
                          handleItemAutocompleteChange(index, "item", val);
                        }}
                        onInputChange={(e, newValue) =>
                          handleItemInputChange(index, item, newValue, "item")
                        }
                        isOptionEqualToValue={(opt, val) =>
                          opt?.id === val?.id || opt?.name === val?.name
                        }
                        renderInput={(params) => <TextField {...params} label="Item" size="small" />}
                      />

                      {/* Description */}
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={item.description}
                        onChange={(e) => handleItemChange(e, index)}
                        size="small"
                      />

                      {/* Unit */}
                      <Autocomplete
                        options={units}
                        getOptionLabel={(option) => option?.name || option?.symbol || ""}
                        value={
                          units.find(
                            (u) =>
                              u.id === item.unit ||
                              u.id === Number(item.unit) ||
                              u.id === item.unit?.id,
                          ) || null
                        }
                        isOptionEqualToValue={(opt, val) =>
                          opt?.id === val?.id || opt?.id === val || opt?.id === Number(val)
                        }
                        onChange={(e, newVal) => {
                          const updatedItems = [...formData.items];
                          updatedItems[index].unit = newVal?.id || "";
                          setFormData((prev) => ({ ...prev, items: updatedItems }));
                        }}
                        renderInput={(params) => <TextField {...params} label="Unit" size="small" />}
                      />

                      {/* Qty */}
                      <TextField
                        fullWidth
                        label="Qty"
                        type="number"
                        name="quantity"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(e, index)}
                        size="small"
                      />

                      {/* Rate */}
                      <TextField
                        fullWidth
                        label="Rate"
                        type="number"
                        name="rate"
                        value={item.rate}
                        onChange={(e) => handleItemChange(e, index)}
                        size="small"
                      />

                      {/* Disc */}
                      <TextField
                        fullWidth
                        label="Disc (%)"
                        type="number"
                        name="dis_per"
                        value={item.dis_per}
                        onChange={(e) => handleItemChange(e, index)}
                        size="small"
                      />

                      {/* Total */}
                      <TextField
                        fullWidth
                        label="Total"
                        value={item.amount}
                        size="small"
                        disabled
                        sx={{ "& .MuiInputBase-root": { bgcolor: disabledBg } }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}

              <Button
                variant="outlined"
                onClick={addItem}
                startIcon={<AddIcon />}
                fullWidth
                sx={{ borderRadius: 0 }}
              >
                Add Item
              </Button>
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                mb: 1,
                overflowX: "auto",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                backgroundImage: "none",
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Box sx={{ minWidth: 1800 }}>
                <Table size="small">
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        color: "text.primary",
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        backgroundColor: `${tableHeadBg} !important`,
                        fontSize: "0.75rem",
                        letterSpacing: "0.04em",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Product Type</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ width: "80px" }}>Unit</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Disc (%)</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor: rowBg(index),
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: isDark
                              ? alpha(theme.palette.primary.main, 0.06)
                              : "#f0f7ff",
                          },
                          "& td": {
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            color: "text.primary",
                          },
                        }}
                      >
                        {/* Category FIRST */}
                        <TableCell sx={{ minWidth: 170 }}>
                          <Autocomplete
                            options={allCategories}
                            getOptionLabel={(opt) => opt?.name || ""}
                            value={item.category || null}
                            onChange={(e, val) => handleRowCategoryChange(index, val)}
                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                inputProps={{
                                  ...params.inputProps,
                                  style: { height: "10px" },
                                }}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Product Type SECOND */}
                        <TableCell sx={{ minWidth: 160 }}>
                          <Autocomplete
                            options={rowProductTypeOptions[index] || []}
                            getOptionLabel={(opt) => opt?.size || ""}
                            value={item.product_type || null}
                            disabled={!item.category}
                            onChange={(e, val) => handleRowProductTypeChange(index, val)}
                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Product Type"
                                inputProps={{
                                  ...params.inputProps,
                                  style: { height: "10px" },
                                }}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Item */}
                        <TableCell>
                          <Autocomplete
                            options={items[index] || (item.item ? [item.item] : [])}
                            getOptionLabel={(opt) => opt?.name || ""}
                            value={
                              (items[index] || []).find(
                                (i) => i.id === item.item?.id || i.id === item.item,
                              ) ||
                              item.item ||
                              null
                            }
                            disabled={!item.product_type}
                            onChange={async (e, val, reason) => {
                              if (reason === "clear" || !val) {
                                await handleItemInputChange(index, item, "", "item");
                                handleItemAutocompleteChange(index, "item", null);
                                return;
                              }
                              handleItemAutocompleteChange(index, "item", val);
                            }}
                            onInputChange={(e, newValue) =>
                              handleItemInputChange(index, item, newValue, "item")
                            }
                            isOptionEqualToValue={(opt, val) =>
                              opt?.id === val?.id || opt?.name === val?.name
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item"
                                inputProps={{
                                  ...params.inputProps,
                                  style: { height: "10px" },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  sx: { paddingRight: "65px !important" },
                                }}
                              />
                            )}
                          />
                        </TableCell>

                        {/* Description */}
                        <TableCell sx={{ width: "200px" }}>
                          <TextField
                            fullWidth
                            name="description"
                            value={item.description}
                            onChange={(e) => handleItemChange(e, index)}
                          />
                        </TableCell>

                        {/* Unit */}
                        <TableCell sx={{ width: "150px" }}>
                          <Grid item minWidth={150}>
                            <Autocomplete
                              options={units}
                              getOptionLabel={(option) => option?.name || option?.symbol || ""}
                              value={
                                units.find(
                                  (u) =>
                                    u.id === item.unit ||
                                    u.id === Number(item.unit) ||
                                    u.id === item.unit?.id,
                                ) || null
                              }
                              isOptionEqualToValue={(opt, val) =>
                                opt?.id === val?.id ||
                                opt?.id === val ||
                                opt?.id === Number(val)
                              }
                              sx={{
                                "& .MuiInputBase-root": {
                                  height: 45,
                                  paddingRight: "8px",
                                },
                                "& .MuiOutlinedInput-input": {
                                  padding: "10px 14px",
                                },
                              }}
                              onChange={(e, newVal) => {
                                const updatedItems = [...formData.items];
                                updatedItems[index].unit = newVal?.id || "";
                                setFormData((prev) => ({
                                  ...prev,
                                  items: updatedItems,
                                }));
                              }}
                              renderInput={(params) => <TextField {...params} label="Unit" />}
                            />
                          </Grid>
                        </TableCell>

                        {/* Qty */}
                        <TableCell sx={{ width: "150px" }}>
                          <TextField
                            fullWidth
                            type="number"
                            name="quantity"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(e, index)}
                          />
                        </TableCell>

                        {/* Rate */}
                        <TableCell sx={{ width: "150px" }}>
                          <TextField
                            fullWidth
                            type="number"
                            name="rate"
                            value={item.rate}
                            onChange={(e) => handleItemChange(e, index)}
                          />
                        </TableCell>

                        {/* Disc */}
                        <TableCell sx={{ width: "150px" }}>
                          <TextField
                            fullWidth
                            type="number"
                            name="dis_per"
                            value={item.dis_per}
                            onChange={(e) => handleItemChange(e, index)}
                          />
                        </TableCell>

                        {/* Total (disabled) */}
                        <TableCell sx={{ width: "150px" }}>
                          <TextField
                            fullWidth
                            value={item.amount}
                            disabled
                            sx={{ "& .MuiInputBase-root": { bgcolor: disabledBg } }}
                          />
                        </TableCell>

                        {/* Delete */}
                        <TableCell>
                          <IconButton
                            onClick={() => deleteItem(index)}
                            color="error"
                            sx={{
                              "&:hover": {
                                bgcolor: (t) =>
                                  t.palette.mode === "dark"
                                    ? alpha(t.palette.error.main, 0.15)
                                    : "#ffebee",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}

          {/* ── Totals Row ── */}
          <Grid container spacing={1} alignItems="center" sx={{ mt: 1, mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={addItem}
                startIcon={<AddIcon />}
                sx={{
                  height: 32,
                  fontSize: { xs: "0.6rem", sm: "0.65rem" },
                  borderRadius: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Add Item
              </Button>
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Sub Total"
                value={formData.total_amount}
                disabled
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 32,
                    borderRadius: 0,
                    bgcolor: disabledBg,
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="CGST+SGST (%)"
                type="number"
                value={formData.cgst_per || 0}
                onChange={(e) => {
                  const cgst_per = parseFloat(e.target.value) || 0;
                  const cgst_amt = (formData.total_amount * cgst_per) / 100;
                  const sgst_amt =
                    (formData.total_amount * (formData.sgst_per || 0)) / 100;
                  const grand_total =
                    formData.total_amount +
                    cgst_amt +
                    sgst_amt +
                    Number(formData.round_off || 0);
                  setFormData((prev) => ({
                    ...prev,
                    cgst_per,
                    grand_total: Number(grand_total.toFixed(2)),
                  }));
                }}
                size="small"
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="IGST (%)"
                type="number"
                value={formData.sgst_per || 0}
                onChange={(e) => {
                  const sgst_per = parseFloat(e.target.value) || 0;
                  const cgst_amt =
                    (formData.total_amount * (formData.cgst_per || 0)) / 100;
                  const sgst_amt = (formData.total_amount * sgst_per) / 100;
                  const grand_total =
                    formData.total_amount +
                    cgst_amt +
                    sgst_amt +
                    Number(formData.round_off || 0);
                  setFormData((prev) => ({
                    ...prev,
                    sgst_per,
                    grand_total: Number(grand_total.toFixed(2)),
                  }));
                }}
                size="small"
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Round Off"
                type="number"
                value={formData.round_off || 0}
                onChange={(e) => {
                  const round_off = parseFloat(e.target.value) || 0;
                  const cgst_amt =
                    (formData.total_amount * (formData.cgst_per || 0)) / 100;
                  const sgst_amt =
                    (formData.total_amount * (formData.sgst_per || 0)) / 100;
                  const grand_total =
                    formData.total_amount + cgst_amt + sgst_amt + round_off;
                  setFormData((prev) => ({
                    ...prev,
                    round_off,
                    grand_total: Number(grand_total.toFixed(2)),
                  }));
                }}
                size="small"
                sx={{
                  "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="Grand Total"
                value={formData.grand_total || 0}
                disabled
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: 32,
                    borderRadius: 0,
                    bgcolor: grandTotalBg,
                    fontWeight: 700,
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: "divider" }} />

          {/* ── Terms and Conditions ── */}
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{
                mb: 1,
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                color: "text.primary",
              }}
            >
              Terms & Conditions
            </Typography>

            {termsAndConditions.map((term, index) => (
              <Grid container spacing={0.5} key={index} alignItems="center" sx={{ mb: 0.5 }}>
                <Grid item xs={3.5} sm={3}>
                  <TextField
                    fullWidth
                    value={term.key}
                    onChange={(e) => handleTermChange(index, "key", e.target.value)}
                    placeholder="Term"
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                  />
                </Grid>
                <Grid item xs={7} sm={8}>
                  <TextField
                    fullWidth
                    value={term.value}
                    onChange={(e) => handleTermChange(index, "value", e.target.value)}
                    placeholder="Value"
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
                      "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                    }}
                  />
                </Grid>
                <Grid item xs={1.5} sm={1} sx={{ display: "flex", justifyContent: "center" }}>
                  <IconButton
                    onClick={() => removeTerm(index)}
                    color="error"
                    size="small"
                    sx={{
                      p: 0.3,
                      "&:hover": {
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addTerm}
              sx={{ mt: 1, height: 30, fontSize: "0.65rem", borderRadius: 0 }}
              size="small"
            >
              Add Term
            </Button>
          </Box>

          {isMobile && <Box sx={{ height: 30 }} />}
        </Box>
      </form>

      {/* Add Item Dialog */}
      <Dialog
        open={openItemModal}
        onClose={handleCloseItemModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            fontWeight: 700,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
          }}
        >
          Add Item
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.paper" }}>
          <AddItemForm onClose={handleCloseItemModal} />
        </DialogContent>
      </Dialog>
    </Container>
  );
}